import { useEffect, useRef, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Camera, X, ScanLine } from 'lucide-react';

export default function BarcodeScanner({ onScan, onClose, isOpen }) {
  const [isScanning, setIsScanning] = useState(false);
  const [error, setError] = useState(null);
  const scannerRef = useRef(null);
  const html5QrCodeRef = useRef(null);

  useEffect(() => {
    if (isOpen && !isScanning) {
      startScanning();
    }
    
    return () => {
      stopScanning();
    };
  }, [isOpen]);

  const startScanning = async () => {
    setError(null);
    
    try {
      const html5QrCode = new Html5Qrcode("barcode-reader");
      html5QrCodeRef.current = html5QrCode;
      
      await html5QrCode.start(
        { facingMode: "environment" }, // Use back camera
        {
          fps: 10,
          qrbox: { width: 250, height: 150 },
          aspectRatio: 1.777778
        },
        (decodedText, decodedResult) => {
          // On successful scan
          onScan(decodedText);
          stopScanning();
        },
        (errorMessage) => {
          // Ignore scan errors (continuous scanning)
        }
      );
      
      setIsScanning(true);
    } catch (err) {
      console.error("Scanner error:", err);
      setError(err.message || "Failed to start camera. Please ensure camera permissions are granted.");
    }
  };

  const stopScanning = async () => {
    if (html5QrCodeRef.current && isScanning) {
      try {
        await html5QrCodeRef.current.stop();
        html5QrCodeRef.current = null;
      } catch (err) {
        console.error("Error stopping scanner:", err);
      }
    }
    setIsScanning(false);
  };

  const handleClose = () => {
    stopScanning();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-white">
        <CardContent className="p-4">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <ScanLine className="w-5 h-5 text-[#007AFF]" />
              <span className="font-medium">Scan Barcode</span>
            </div>
            <Button variant="ghost" size="sm" onClick={handleClose}>
              <X className="w-5 h-5" />
            </Button>
          </div>

          {/* Scanner Area */}
          <div className="relative">
            <div 
              id="barcode-reader" 
              ref={scannerRef}
              className="w-full rounded-lg overflow-hidden bg-black"
              style={{ minHeight: '280px' }}
            />
            
            {/* Scanning overlay */}
            {isScanning && (
              <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
                <div className="w-64 h-32 border-2 border-[#007AFF] rounded-lg relative">
                  <div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-[#007AFF]"></div>
                  <div className="absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2 border-[#007AFF]"></div>
                  <div className="absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2 border-[#007AFF]"></div>
                  <div className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-[#007AFF]"></div>
                  {/* Scanning line animation */}
                  <div className="absolute inset-x-2 h-0.5 bg-[#007AFF] animate-scan"></div>
                </div>
              </div>
            )}
          </div>

          {/* Error message */}
          {error && (
            <div className="mt-4 p-3 bg-[#FF3B30]/10 text-[#FF3B30] rounded-lg text-sm">
              {error}
            </div>
          )}

          {/* Instructions */}
          <p className="mt-4 text-center text-sm text-[#0A0A0A]/60">
            Point your camera at a barcode to scan
          </p>

          {/* Manual entry option */}
          <Button 
            variant="outline" 
            className="w-full mt-4"
            onClick={handleClose}
          >
            Enter Manually
          </Button>
        </CardContent>
      </Card>

      <style jsx>{`
        @keyframes scan {
          0%, 100% { top: 10%; }
          50% { top: 90%; }
        }
        .animate-scan {
          animation: scan 2s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}
